import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getEnv } from '@hub/helpers/getters.store';
import { useAppInitialization } from '@hub/hooks';
import { trackMixpanel } from '@monorepo/client-common';
import { PagePath } from '@monorepo/client-types';
import { LanguageDropdown } from '@monorepo/hub-common';
import {
  CheckoutSource,
  EventTypes,
  IDP_PROVIDER,
  IdpProviderKey,
  IsLoggedOutKey,
  RegisterRequest,
} from '@monorepo/types';
import { OTP_MAX_RETRIES_REDIRECT_DELAY_MS } from '../constants/quick-purchase.constants';
import { QuickPurchaseError } from '../hooks/use-quick-purchase-flow';
import { useQuickPurchaseOtp } from '../hooks/use-quick-purchase-otp';
import { useResendCooldown } from '../hooks/use-resend-cooldown';
import { QuickPurchaseCountryStep } from './quick-purchase-country-step';
import { QuickPurchaseEmailStep } from './quick-purchase-email-step';
import {
  StyledDialog,
  StyledDialogContent,
  StyledLanguageSelectorContainer,
} from './quick-purchase-modal.styles';
import { QuickPurchaseOtpStep } from './quick-purchase-otp-step';

enum ModalStep {
  Email = 'email',
  Otp = 'otp',
  Country = 'country',
}

interface EmailFormValues {
  email: string;
}

interface OtpFormValues {
  code: string;
}

interface CountryFormValues {
  country: RegisterRequest['country'];
}

interface QuickPurchaseModalProps {
  onError: (error: QuickPurchaseError) => void;
  onAuthenticated: (userId: string) => void;
}

export const QuickPurchaseModal: FC<QuickPurchaseModalProps> = ({
  onError,
  onAuthenticated,
}) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { forceInit } = useAppInitialization();
  const isMounted = useRef(true);

  const {
    sendOtp,
    verifyOtp,
    status,
    errorMessage,
    maxRetriesExceeded,
    sessionId,
    clearError,
  } = useQuickPurchaseOtp();

  const [modalStep, setModalStep] = useState<ModalStep>(ModalStep.Email);
  const [email, setEmail] = useState('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const { cooldownSeconds, startCooldown } = useResendCooldown();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Redirect to login when max OTP retries are exceeded
  useEffect(() => {
    if (maxRetriesExceeded) {
      const timer = setTimeout(() => {
        localStorage.setItem('initialUrl', '/purchase?step=assets');
        void getEnv().apiFactory.quickPurchaseService.logRedirect(
          'max_otp_retries',
        );
        navigate(PagePath.Login, { replace: true });
      }, OTP_MAX_RETRIES_REDIRECT_DELAY_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [maxRetriesExceeded, navigate]);

  const emailForm = useForm<EmailFormValues>({ defaultValues: { email: '' } });
  const otpForm = useForm<OtpFormValues>({ defaultValues: { code: '' } });
  const countryForm = useForm<CountryFormValues>({
    defaultValues: { country: '' },
  });

  const finalizeSession = useCallback(
    async (userId: string, sessionJwt: string) => {
      localStorage.removeItem(IsLoggedOutKey);
      // Session cookie is a Descope anonymous JWT — must use DESCOPE strategy
      localStorage.setItem(IdpProviderKey, IDP_PROVIDER.DESCOPE);

      // Set DS cookie from the frontend so it's accessible on page refresh.
      // The API also sets this cookie, but it's on a different domain (api-*.the5ers.com)
      // and won't be readable via document.cookie from the Hub domain (hub-*.the5ers.com).
      // Setting it here ensures getDescopeSessionToken() can read it after page refresh.
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      const expires = new Date(Date.now() + sevenDaysMs).toUTCString();
      document.cookie = `DS=${encodeURIComponent(sessionJwt)}; path=/; expires=${expires}; secure; samesite=none`;

      // The SDK can't provide the token yet — inject it directly so forceInit's
      // requests are authenticated. The interceptor takes over once the SDK is ready.
      const { apiFactory } = getEnv();
      apiFactory.setAuthorizationHeader(sessionJwt);

      try {
        await forceInit();
      } catch {
        // forceInit errors are non-fatal — userStore.setLoginState(true) is
        // called inside init() before any error, so userId will be available.
      }

      // forceInit calls setLoginState(true) mid-execution, swapping the router
      // and unmounting this component. If already unmounted, the new
      // QuickPurchasePage handles the flow via its own initFlow() effect.
      if (isMounted.current) {
        onAuthenticated(userId);
      }
    },
    [forceInit, onAuthenticated],
  );

  const handleEmailSubmit = useCallback(
    async ({ email: submittedEmail }: EmailFormValues) => {
      const normalizedEmail = submittedEmail.toLowerCase().trim();
      const { sent } = await sendOtp(normalizedEmail);
      if (sent) {
        setEmail(normalizedEmail);
        setModalStep(ModalStep.Otp);
        startCooldown();
      }
      // On failure, errorMessage state is set by the hook and shown inline
    },
    [sendOtp, startCooldown],
  );

  const handleOtpSubmit = useCallback(
    async ({ code }: OtpFormValues) => {
      if (maxRetriesExceeded) return;

      const { verified, isExistingUser } = await verifyOtp(code);

      if (!verified) {
        return;
      }

      if (isExistingUser) {
        // Existing user: skip country step, create anonymous session immediately
        if (!sessionId) return;
        setIsCreatingSession(true);
        try {
          const { userId, sessionJwt } =
            await getEnv().apiFactory.quickPurchaseService.createAnonymousUser(
              sessionId,
              email,
            );
          trackMixpanel({
            event: EventTypes.QUICK_PURCHASE_SESSION_CREATED,
            properties: {
              checkout_source: CheckoutSource.QuickPurchase,
              is_existing_user: true,
            },
          });
          await finalizeSession(userId, sessionJwt);
        } catch (err) {
          const message = (err as Error)?.message || '';
          if (message.includes('EXISTING_USER_MISSING_PROFILE')) {
            onError(QuickPurchaseError.ExistingUserMissingProfile);
          } else if (message.includes('OTP_EMAIL_MISMATCH')) {
            onError(QuickPurchaseError.OtpEmailMismatch);
          } else {
            // Show error modal on skeleton first; parent's useEffect handles the
            // delayed redirect to login (see quick-purchase-page.tsx).
            onError(QuickPurchaseError.Generic);
          }
        } finally {
          setIsCreatingSession(false);
        }
      } else {
        // New user: show country step
        setModalStep(ModalStep.Country);
      }
    },
    [email, verifyOtp, maxRetriesExceeded, sessionId, finalizeSession, onError],
  );

  const handleCountrySubmit = useCallback(
    async ({ country }: CountryFormValues) => {
      if (!sessionId) return;
      setIsCreatingSession(true);
      try {
        const { userId, sessionJwt } =
          await getEnv().apiFactory.quickPurchaseService.createAnonymousUser(
            sessionId,
            email,
            country,
          );
        trackMixpanel({
          event: EventTypes.QUICK_PURCHASE_SESSION_CREATED,
          properties: {
            checkout_source: CheckoutSource.QuickPurchase,
            is_existing_user: false,
          },
        });
        await finalizeSession(userId, sessionJwt);
      } catch (err) {
        const message = (err as Error)?.message || '';
        if (message.includes('EMAIL_ALREADY_EXISTS')) {
          setModalStep(ModalStep.Email);
        } else if (message.includes('OTP_EMAIL_MISMATCH')) {
          onError(QuickPurchaseError.OtpEmailMismatch);
        } else {
          // Show error modal on skeleton first; parent's useEffect handles the
          // delayed redirect to login (see quick-purchase-page.tsx).
          onError(QuickPurchaseError.Generic);
        }
      } finally {
        setIsCreatingSession(false);
      }
    },
    [sessionId, email, finalizeSession, onError],
  );

  const isLoading =
    status === 'sending' || status === 'verifying' || isCreatingSession;

  const handleResend = useCallback(async () => {
    if (!email || isLoading || maxRetriesExceeded || cooldownSeconds > 0)
      return;
    const { sent } = await sendOtp(email);
    if (sent) {
      startCooldown();
    }
  }, [
    email,
    isLoading,
    maxRetriesExceeded,
    cooldownSeconds,
    sendOtp,
    startCooldown,
  ]);

  const DISPLAY_LANGUAGE_SELECTOR = false;
  return (
    <StyledDialog open disableEscapeKeyDown>
      <StyledDialogContent>
        {modalStep === ModalStep.Email && DISPLAY_LANGUAGE_SELECTOR && (
          <StyledLanguageSelectorContainer>
            <LanguageDropdown
              openDownward
              hideLabel
              noBlur
              onLanguageChange={(lang) => i18n.changeLanguage(lang)}
            />
          </StyledLanguageSelectorContainer>
        )}
        {modalStep === ModalStep.Email && (
          <QuickPurchaseEmailStep
            emailForm={emailForm}
            isLoading={isLoading}
            errorMessage={errorMessage}
            onSubmit={handleEmailSubmit}
          />
        )}

        {modalStep === ModalStep.Otp && (
          <QuickPurchaseOtpStep
            otpForm={otpForm}
            email={email}
            status={status}
            isLoading={isLoading}
            maxRetriesExceeded={maxRetriesExceeded}
            resendCooldownSeconds={cooldownSeconds}
            onSubmit={handleOtpSubmit}
            onBack={() => {
              clearError();
              otpForm.reset();
              setModalStep(ModalStep.Email);
            }}
            onResend={handleResend}
            onClearError={clearError}
          />
        )}

        {modalStep === ModalStep.Country && (
          <QuickPurchaseCountryStep
            countryForm={countryForm}
            isLoading={isLoading}
            onSubmit={handleCountrySubmit}
          />
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};
